/**
 * Session persisted in localStorage: tokens, the selected stable (tenant slug) and UI preferences.
 * A tiny observable store so the API client and React share one source of truth.
 */
export interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface SessionState {
  tokens: Tokens | null;
  tenant: string | null;
  /** Remembered from the login response so the platform console works before a stable is chosen. */
  platformAdmin?: boolean;
}

const KEY = 'stable.session';
const listeners = new Set<() => void>();

function read(): SessionState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as SessionState;
  } catch {
    // ignore corrupt storage
  }
  return { tokens: null, tenant: null };
}

let state: SessionState = read();

export const session = {
  get(): SessionState {
    return state;
  },
  set(patch: Partial<SessionState>): void {
    state = { ...state, ...patch };
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      // storage may be unavailable (private mode) — the session still works in memory
    }
    listeners.forEach((l) => l());
  },
  clear(): void {
    session.set({ tokens: null, tenant: null, platformAdmin: false });
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

window.addEventListener('storage', (e) => {
  if (e.key === KEY) {
    state = read();
    listeners.forEach((l) => l());
  }
});
